import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ControlContext } from "../../lib/control-context.js";
import { cn } from "../../lib/utils.js";
import { useTransitionPresence } from "../../hooks/useTransitionPresence.js";
import { MOTION_DURATION_MS } from "../../motion.js";
import {
  getLayerRoot,
  useDismissable,
  useLayer,
  usePosition,
} from "../../primitives/index.js";

/* ---------------- Tooltip ----------------
   Engine-backed since increment 5.4 (was CSS-only group-hover with raw z-50,
   no portal — it clipped inside any overflow container). Now: portals into
   the layer root at z-tooltip, places via usePosition (flips when clipped),
   hover-intent delay, instant show on keyboard focus, Esc dismiss (APG).

   The measuring wrapper <span> keeps the old API promise: ANY child works —
   no ref-forwarding demanded (Button doesn't forward refs yet). Only
   aria-describedby is cloned onto the child for AT association. */

/** `md` (default) is today's one-line chip. `lg` is rich, wrapping content —
 *  see the `size` prop below for the exact classes each renders. */
export type TooltipSize = "md" | "lg";

export interface TooltipProps {
  content: React.ReactNode;
  /** Suppress the tooltip without remounting its interactive child. */
  disabled?: boolean;
  placement?: "top" | "bottom" | "left" | "right" | undefined;
  /** Hover-intent delay before showing, in ms. Keyboard focus shows
   *  immediately; hide is always immediate. */
  delayMs?: number;
  /** Gap between the trigger and tooltip. Uses the positioning-engine default. */
  sideOffset?: number;
  /** Re-measure while open so transformed/animating anchors stay attached. */
  followAnchor?: boolean;
  /** Layout overrides for the passive trigger-measuring wrapper. */
  wrapperClassName?: string;
  wrapperStyle?: React.CSSProperties;
  /** `md` (default) is today's one-line chip: `whitespace-nowrap px-2 py-1
   *  text-xs`. `lg` is rich content: `whitespace-normal break-words
   *  [overflow-wrap:anywhere] text-start p-3 text-body-large`, width capped
   *  at `max-w-[min(20rem,calc(100vw-2rem))]`. The surface (border,
   *  background, shadow), placement, delay, Esc dismissal and motion stay
   *  the same at both sizes. */
  size?: TooltipSize;
  /** Width and layout overrides only (`w-*`, `min-w-*`, `max-w-*`) — for
   *  example widening `lg` past its default cap. Padding, whitespace and
   *  typography through this prop are deprecated: they now come only from
   *  `size`, so a width-only use (the presence roster's) stays valid. No
   *  `@deprecated` tag on the prop itself — only that narrower use is. */
  contentClassName?: string;
  /** Clone `aria-describedby` onto the child while the tooltip shows. Default
   *  true. Pass false when the child already shows the tooltip's text (a
   *  truncated label), so a screen reader does not read it twice. */
  describeChild?: boolean;
  children: React.ReactElement;
}

export function Tooltip({
  content,
  disabled = false,
  placement = "top",
  delayMs = 300,
  sideOffset,
  followAnchor = false,
  wrapperClassName,
  wrapperStyle,
  size = "md",
  contentClassName,
  describeChild = true,
  children,
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  // Reset transient hover state before committing a disabled tooltip, keeping
  // the child mounted and preventing the old hover from returning on enable.
  if (disabled && visible) setVisible(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const tipId = useId();
  const timerRef = useRef<number | undefined>(undefined);

  const isVisible = visible && !disabled;
  /* Retained for one short frame so the tooltip can fade out (ADR-0091). The
     LOGICAL state still flips at once — the layer, the dismiss engine and
     aria-describedby all follow `isVisible`, so a closing tooltip is already
     unannounced and owns nothing; only the paint lingers. */
  const { present, phase } = useTransitionPresence(
    isVisible,
    MOTION_DURATION_MS.fast,
  );
  const layerId = useLayer({ open: isVisible, kind: "tooltip" });
  const insideRefs = useMemo(() => [wrapRef, tipRef], []);
  const onDismiss = useCallback(() => {
    window.clearTimeout(timerRef.current);
    setVisible(false);
  }, []);
  useDismissable({
    layerId,
    open: isVisible,
    onDismiss,
    insideRefs,
    outsidePress: false, // hover/focus loss already hides it
    /* Esc dismisses THIS tooltip whether or not another layer opened after it.
       Two can be up at once — the pointer rests on one trigger while the
       keyboard is on another — and under top-layer gating one Esc left the
       other one on screen. A tooltip owns no focus and consumes nothing, so a
       dialog above it still closes on the same key. */
    escapeRequiresTopLayer: false,
  });

  const pos = usePosition({
    open: present,
    triggerRef: wrapRef,
    contentRef: tipRef,
    side: placement,
    align: "center",
    ...(sideOffset !== undefined ? { sideOffset } : {}),
    followAnchor,
  });

  const showSoon = () => {
    if (disabled) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setVisible(true), delayMs);
  };
  const showNow = () => {
    if (disabled) return;
    window.clearTimeout(timerRef.current);
    setVisible(true);
  };
  /* Keyboard focus only (`:focus-visible`). A pointer that closes a menu or
     dialog hands focus back to its trigger; that is not a keyboard arrival,
     and a tooltip shown there stayed until the next blur. */
  const showOnKeyboardFocus = (event: React.FocusEvent) => {
    if (isKeyboardFocus(event.target)) showNow();
  };
  const hide = () => {
    window.clearTimeout(timerRef.current);
    setVisible(false);
  };
  useEffect(() => () => window.clearTimeout(timerRef.current), []);
  useEffect(() => {
    window.addEventListener("blur", onDismiss);
    return () => window.removeEventListener("blur", onDismiss);
  }, [onDismiss]);
  useEffect(() => {
    if (disabled) {
      window.clearTimeout(timerRef.current);
    }
  }, [disabled]);

  /* Clone ONLY the AT association onto the child — no ref or handlers. With
     describeChild off, the child passes through untouched. */
  const child =
    describeChild && React.isValidElement(children)
      ? React.cloneElement(
          children as React.ReactElement<{
            "aria-describedby"?: string | undefined;
          }>,
          { "aria-describedby": isVisible ? tipId : undefined },
        )
      : children;

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions --
       passive hover/focus-sensing wrapper (APG tooltip pattern): the
       interactive element is the CHILD; keyboard access is the child's own
       focus (shows instantly), Esc dismissal goes through the engine. */
    <span
      ref={wrapRef}
      className={cn("relative inline-flex", wrapperClassName)}
      style={wrapperStyle}
      onMouseEnter={showSoon}
      onMouseLeave={hide}
      onPointerLeave={hide}
      onPointerCancel={hide}
      onFocus={showOnKeyboardFocus}
      onBlur={hide}
      // Activation can leave focus and the pointer on the trigger while its
      // panel opens. Capture also handles children that stop click bubbling.
      onClickCapture={hide}
    >
      {child}
      {present &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={tipRef}
            id={tipId}
            role="tooltip"
            data-motion-state={phase}
            {...(phase === "closing" ? { "aria-hidden": "true" as const } : {})}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
            className={cn(
              "pointer-events-none z-tooltip rounded-md border border-pb-border",
              "bg-pb-card-elevated text-pb-text shadow-md",
              /* Rises in, and on the way out only fades, quickly (ADR-0091):
                 a tooltip is the lightest and most frequent overlay, and one
                 that slid away would read as a menu. */
              "pb-motion-tooltip",
              size === "lg"
                ? "max-w-[min(20rem,calc(100vw-2rem))] whitespace-normal break-words [overflow-wrap:anywhere] text-start p-3 text-body-large"
                : "whitespace-nowrap px-2 py-1 text-xs",
              contentClassName,
            )}
          >
            {/* Portalled out of any rail group: a Button here keeps its look. */}
            <ControlContext.Provider value={null}>
              {content}
            </ControlContext.Provider>
          </div>,
          getLayerRoot(),
        )}
    </span>
  );
}

/** A browser that cannot parse `:focus-visible` keeps show-on-any-focus, so a
 *  keyboard user still gets the tooltip. */
function isKeyboardFocus(element: Element): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
}
